/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useEffect, useState } from "react";
import { AppContext } from "../../../utils/AppContext";
import {
  IIntegrationTestConfiguration,
  useIntegrationTestConfigurationService,
} from "../../../services/registry/IntegrationTestConfigurationService";
import { IntegrationTestConfigurationsTable } from "./IntegrationTestConfigurationTable";
import { AlertColor } from "@mui/material";
import { Notify } from "../../../components/Notify";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import { YesNoCancelPopup } from "../../../components/YesNoCancelPopup";
import {
  IntegrationTestConfigurationForm,
  IntegrationTestConfigurationFormData,
  IntegrationTestConfigurationFormType,
} from "./IntegrationTestConfigurationForm";

const IntegrationTestConfiguration = () => {
  const integrationTestConfigurationService = useIntegrationTestConfigurationService();

  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [integrationTestConfigurations, setIntegrationTestConfigurations] = useState<IIntegrationTestConfiguration[]>([]);
  const [selectedItem, setSelectedItem] = useState<IIntegrationTestConfiguration | undefined>(undefined);
  const [integrationTestConfigurationFormVisible, setIntegrationTestConfigurationFormVisible] = useState(false);
  const [integrationTestConfigurationFormType, setIntegrationTestConfigurationFormType] =
    useState<IntegrationTestConfigurationFormType>("new");
  const [deleteConfigurationPopupVisible, setDeleteConfigurationPopupVisible] = useState(false);

  const context = React.useContext(AppContext);
  useEffect(() => {
    context?.setTitle("Integration Test Configuration");
  }, [context]);

  useEffect(() => {
    (async () => fetchIntegrationTestConfigurations())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchIntegrationTestConfigurations = async () => {
    try {
      setLoading(true);
      const { list } = await integrationTestConfigurationService.list();
      setIntegrationTestConfigurations(list);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to list Integration Test Configurations.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const integrationTestConfigurationFormAcceptHandler = async (data: IntegrationTestConfigurationFormData) => {
    if (integrationTestConfigurationFormType === "new") {
      try {
        await integrationTestConfigurationService.create(
          data.clientBranch,
          data.backendImageId,
          data.activePackageId,
          data.activeSchemaBranch,
          data.activeSchemaVersion,
        );
        setIntegrationTestConfigurationFormVisible(false);
        await fetchIntegrationTestConfigurations();
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not add the new integration test configuration.");
        setAlertSeverity("error");
      }
    } else {
      try {
        await integrationTestConfigurationService.update(
          data.clientBranch,
          data.backendImageId,
          data.activePackageId,
          data.activeSchemaBranch,
          data.activeSchemaVersion,
        );
        setIntegrationTestConfigurationFormVisible(false);
        await fetchIntegrationTestConfigurations();
      } catch (e: any) {
        setAlertMessage(e.debugMessage || "Could not edit the integration test configuration.");
        setAlertSeverity("error");
      }
    }
  };

  const deleteConfigurationPopupYesHandler = async () => {
    if (!selectedItem) {
      return;
    }

    try {
      setLoading(true);
      setDeleteConfigurationPopupVisible(false);

      await integrationTestConfigurationService.remove(selectedItem.clientBranch);
      setSelectedItem(undefined);
      await fetchIntegrationTestConfigurations();
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not remove integration test configuration.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const onIntegrationTestConfigurationAddButtonClick = () => {
    setIntegrationTestConfigurationFormType("new");
    setIntegrationTestConfigurationFormVisible(true);
  };

  const onIntegrationTestConfigurationEditButtonClick = () => {
    setIntegrationTestConfigurationFormType("edit");
    setIntegrationTestConfigurationFormVisible(true);
  };

  return (
    <>
      <IntegrationTestConfigurationsTable
        rows={integrationTestConfigurations}
        selectedItem={selectedItem}
        setSelectedItem={setSelectedItem}
        onAddButtonClick={onIntegrationTestConfigurationAddButtonClick}
        onEditButtonClick={onIntegrationTestConfigurationEditButtonClick}
        onRemoveButtonClick={() => setDeleteConfigurationPopupVisible(true)}
      />
      <IntegrationTestConfigurationForm
        visible={integrationTestConfigurationFormVisible}
        type={integrationTestConfigurationFormType}
        initialData={integrationTestConfigurationFormType === "edit" ? selectedItem : undefined}
        availableConfigurations={integrationTestConfigurations}
        onDismiss={() => setIntegrationTestConfigurationFormVisible(false)}
        onAccept={integrationTestConfigurationFormAcceptHandler}
      />
      <YesNoCancelPopup
        visible={deleteConfigurationPopupVisible}
        title={"Remove Integration Test Configuration"}
        message={`Are you sure you want to remove the Integration Test Configuration for <b>${selectedItem?.clientBranch}</b>?<br/>
        This Integration Test Configuration will become permanently unavailable if you remove it.`}
        onNo={() => setDeleteConfigurationPopupVisible(false)}
        onYes={deleteConfigurationPopupYesHandler}
      />
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default IntegrationTestConfiguration;
